import React, { createContext, useState, useContext, useCallback } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import {
  ACCOUNT_SELECTION_KEY,
  applyAccountSelection,
  getPendingAccountSelection,
  getCurrentAccountSelection,
  saveAccountSelection,
} from './accountSelection';

// Create the context
const SelectedProviderContext = createContext();

// Create a provider component
export const SelectedProviderProvider = ({ children }) => {
  const [selectedAccount, setSelectedAccount] = useState(getCurrentAccountSelection);
  const selectedProvider = selectedAccount.provider;
  const activeAccountId = selectedAccount.accountId;
  const [accountChange, setAccountChange] = useState(null);
  const [syncError, setSyncError] = useState("");

  const checkAccountSelection = useCallback(() => {
    try {
      setAccountChange(getPendingAccountSelection());
      setSyncError("");
    } catch (error) {
      console.error("Unable to synchronize account selection:", error);
      setSyncError(error.message);
    }
  }, []);

  React.useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === ACCOUNT_SELECTION_KEY) checkAccountSelection();
    };
    checkAccountSelection();
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", checkAccountSelection);
    window.addEventListener("accountSelectionRequired", checkAccountSelection);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", checkAccountSelection);
      window.removeEventListener("accountSelectionRequired", checkAccountSelection);
    };
  }, [checkAccountSelection]);

  const selectAccount = (project, providerId, accountId, response) => {
    const selection = saveAccountSelection(project, providerId, accountId, response);
    setSelectedAccount(selection);
  };

  const acceptAccountChange = () => {
    try {
      // Read again in case another account switch occurred while the warning was open.
      const latestSelection = getPendingAccountSelection();
      if (latestSelection) applyAccountSelection(latestSelection);
      window.location.assign("/Resources");
    } catch (error) {
      console.error("Unable to apply shared account selection:", error);
      setSyncError(error.message);
    }
  };

  const resetSelectedProvider = () => {
    localStorage.removeItem("selectedProvider");
    setSelectedAccount(previous => ({ ...previous, provider: null }));
  };

  const resetActiveAccountId = () => {
    sessionStorage.removeItem("activeAccountId");
    setSelectedAccount(previous => ({ ...previous, accountId: null }));
  };

  const updateActiveAccountId = (id) => {
    if (id) sessionStorage.setItem("activeAccountId", id);
    else sessionStorage.removeItem("activeAccountId");
    setSelectedAccount(previous => ({ ...previous, accountId: id }));
  };

  const updateSelectedProvider = (provider) => {
    if (provider) localStorage.setItem("selectedProvider", provider);
    else localStorage.removeItem("selectedProvider");
    setSelectedAccount(previous => ({ ...previous, provider }));
  };

  return (
    <SelectedProviderContext.Provider value={{ 
      selectedProvider, 
      selectedAccount,
      setSelectedProvider: updateSelectedProvider, 
      resetSelectedProvider,
      activeAccountId,
      setActiveAccountId: updateActiveAccountId,
      selectAccount,
      resetActiveAccountId
    }}>
      {children}
      <Dialog
        open={Boolean(accountChange || syncError)}
        disableEscapeKeyDown
        sx={{ zIndex: theme => theme.zIndex.modal + 100 }}
        aria-labelledby="account-change-title"
      >
        <DialogTitle id="account-change-title">Account changed in another tab</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {syncError || `Another tab selected ${accountChange?.provider} account "${accountChange?.accountName}" (${accountChange?.accountId}). All tabs must use the same account. Requests in this tab are paused. Continuing will reload Resources and discard unsaved changes in this tab.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={syncError ? checkAccountSelection : acceptAccountChange}>
            {syncError ? "Retry synchronization" : "Discard unsaved changes and continue"}
          </Button>
        </DialogActions>
      </Dialog>
    </SelectedProviderContext.Provider>
  );
};

// Custom hook to use the selected provider context
export const useSelectedProvider = () => useContext(SelectedProviderContext);
